import { DoctorLayout } from "@/components/layout/doctor-layout";
import { useState, useEffect, useCallback } from "react";
import { BookOpen, ChevronDown, ChevronUp, CheckCircle2, Clock, Circle, Loader2, Save } from "lucide-react";

type CurriculumStatus = "not_started" | "in_progress" | "achieved";

interface CurriculumEntry {
  domainKey: string;
  competencyKey: string;
  status: CurriculumStatus;
  evidenceNotes: string | null;
}

interface Competency {
  key: string;
  label: string;
  desc?: string;
}

interface Domain {
  key: string;
  title: string;
  competencies: Competency[];
}

const CURRICULUM: Domain[] = [
  {
    key: "basic_science",
    title: "Basic Science & Immunology",
    competencies: [
      { key: "immunopathology", label: "Immunopathology of rheumatic diseases", desc: "Understand the role of innate and adaptive immunity in RA, SLE, vasculitis, and other autoimmune conditions" },
      { key: "genetics", label: "Genetics of autoimmunity", desc: "HLA associations, polygenic risk, genetic counselling principles" },
      { key: "cytokines", label: "Cytokine biology and biologic targets", desc: "TNF, IL-6, IL-17, JAK-STAT pathways and their therapeutic implications" },
      { key: "pharmacology", label: "Advanced pharmacology of DMARDs", desc: "MOA, pharmacokinetics, drug monitoring for conventional and biologic DMARDs" },
    ],
  },
  {
    key: "clinical_assessment",
    title: "Clinical Assessment",
    competencies: [
      { key: "history", label: "Comprehensive rheumatology history", desc: "MSK history, systemic review, red flags, occupational and functional assessment" },
      { key: "examination", label: "Musculoskeletal examination", desc: "GALS screen, regional examination, synovitis scoring, enthesitis and dactylitis" },
      { key: "investigations", label: "Interpretation of investigations", desc: "Autoantibodies, complement, imaging (X-ray, MRI, USS), DEXA, synovial fluid analysis" },
      { key: "diagnosis", label: "Diagnosis and differential diagnosis", desc: "Apply ACR/EULAR classification criteria; construct differential diagnoses" },
    ],
  },
  {
    key: "conditions",
    title: "Conditions",
    competencies: [
      { key: "ra", label: "Rheumatoid Arthritis", desc: "Diagnosis, treat-to-target, NICE TA criteria, biologic escalation, monitoring" },
      { key: "sle", label: "Systemic Lupus Erythematosus", desc: "SLEDAI-2K, organ manifestations, hydroxychloroquine, immunosuppression, pregnancy" },
      { key: "vasculitis", label: "Vasculitis (AAV, GCA, Takayasu)", desc: "ANCA testing, BVAS, remission induction with cyclophosphamide/rituximab, GCA steroid protocols" },
      { key: "axspa", label: "Axial Spondyloarthropathy (axSpA/PsA)", desc: "BASDAI/ASDAS, NICE TA criteria for bDMARDs, imaging (MRI sacroiliitis)" },
      { key: "ctd", label: "Connective Tissue Diseases (SSc, MyOSITIS, Sjogren)", desc: "Nailfold capillaroscopy, HRCT, anti-synthetase syndrome, sicca symptoms" },
      { key: "crystal", label: "Crystal arthropathies", desc: "Gout treat-to-target, urate lowering, CPPD, pseudogout management" },
      { key: "osteoporosis", label: "Osteoporosis & metabolic bone", desc: "DEXA interpretation, fracture risk (FRAX/QFracture), antiresorptive and anabolic therapies" },
      { key: "paediatric", label: "Paediatric rheumatology (JIA & transition)", desc: "JIA subtypes, transition principles, uveitis monitoring, methotrexate in children" },
    ],
  },
  {
    key: "procedures",
    title: "Procedures",
    competencies: [
      { key: "joint_injection", label: "Joint injection (small and large joints)", desc: "Aspiration and injection of knee, shoulder, small hand joints; USS guidance" },
      { key: "aspiration", label: "Synovial fluid analysis", desc: "Macroscopic assessment, microscopy for crystals and cells, interpretation" },
      { key: "skin_biopsy", label: "Skin and lesion assessment", desc: "Recognise rashes, refer appropriately, liaise with dermatology for skin biopsy" },
      { key: "temporal_biopsy", label: "Temporal artery biopsy (supervision/coordination)", desc: "Indication, timing relative to steroids, interpretation of histology" },
    ],
  },
  {
    key: "acute",
    title: "Acute Presentations",
    competencies: [
      { key: "acute_arthritis", label: "Acute hot joint", desc: "Septic arthritis exclusion, gout, pseudogout, reactive arthritis — urgent assessment pathway" },
      { key: "gca_emergency", label: "GCA with visual risk", desc: "Same-day assessment, high-dose IV/oral steroid protocol, ophthalmology liaison" },
      { key: "lupus_flare", label: "Severe SLE flare / lupus nephritis", desc: "Renal biopsy indications, IV methylprednisolone, cyclophosphamide/MMF" },
      { key: "vasculitis_emergency", label: "ANCA vasculitis with organ threat", desc: "Rapid diagnosis, plasma exchange consideration, critical care liaison" },
    ],
  },
  {
    key: "mdt",
    title: "MDT & Communication",
    competencies: [
      { key: "mdt_lead", label: "Leading and participating in MDT", desc: "Present cases, summarise management plans, coordinate care across specialties" },
      { key: "shared_decision", label: "Shared decision-making", desc: "Explain risk/benefit, use decision aids, respect patient autonomy, capacity assessment" },
      { key: "breaking_bad_news", label: "Breaking difficult news", desc: "SPIKES framework, discussing diagnosis, prognosis, treatment limitations" },
      { key: "discharge_planning", label: "Discharge planning and GP communication", desc: "Clear discharge summaries, medication reconciliation, follow-up arrangements" },
    ],
  },
  {
    key: "teaching",
    title: "Teaching & Research",
    competencies: [
      { key: "teaching_delivery", label: "Delivering teaching sessions", desc: "Tutorials, journal clubs, grand rounds, simulation, bedside teaching with feedback" },
      { key: "research_methods", label: "Research methodology", desc: "Study design, statistics, critical appraisal, CONSORT/PRISMA, GCP principles" },
      { key: "audit_qip", label: "Audit and quality improvement", desc: "PDSA cycles, national audit participation (NRAS, BSR biologics register)" },
      { key: "publication", label: "Academic output", desc: "Case reports, original research, review articles, conference presentations" },
    ],
  },
  {
    key: "leadership",
    title: "Leadership & Management",
    competencies: [
      { key: "service_management", label: "Clinical service management", desc: "Understanding NHS structures, job planning, referral pathways, waiting list management" },
      { key: "governance", label: "Clinical governance & patient safety", desc: "Significant event analysis, root cause analysis, national patient safety alerts" },
      { key: "leadership_style", label: "Personal leadership style", desc: "Self-awareness, team dynamics, 360 feedback, adaptive leadership" },
      { key: "appraisal_revalidation", label: "Appraisal and revalidation readiness", desc: "GMC supporting information, appraiser skills, consultant job planning" },
    ],
  },
];

const STATUS_CONFIG = {
  not_started: { label: "Not Started", icon: Circle, color: "text-slate-400 bg-slate-50 border-slate-200" },
  in_progress: { label: "In Progress", icon: Clock, color: "text-amber-600 bg-amber-50 border-amber-200" },
  achieved: { label: "Achieved", icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
};

interface CompetencyRowProps {
  domain: Domain;
  competency: Competency;
  entry: CurriculumEntry | undefined;
  onSave: (domainKey: string, competencyKey: string, status: CurriculumStatus, notes: string) => Promise<void>;
}

function CompetencyRow({ domain, competency, entry, onSave }: CompetencyRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState<CurriculumStatus>(entry?.status ?? "not_started");
  const [notes, setNotes] = useState(entry?.evidenceNotes ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setStatus(entry?.status ?? "not_started");
    setNotes(entry?.evidenceNotes ?? "");
  }, [entry]);

  const cfg = STATUS_CONFIG[status];
  const Icon = cfg.icon;

  const handleSave = async () => {
    setSaving(true);
    await onSave(domain.key, competency.key, status, notes);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
      >
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium shrink-0 ${cfg.color}`}>
          <Icon className="h-3 w-3" />
          {cfg.label}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">{competency.label}</p>
          {competency.desc && <p className="text-xs text-slate-500 truncate">{competency.desc}</p>}
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-2 space-y-3 border-t border-slate-100 bg-slate-50/50">
          {competency.desc && <p className="text-sm text-slate-600">{competency.desc}</p>}
          <div>
            <p className="block text-xs font-medium text-slate-600 mb-1.5">Status</p>
            <div className="flex gap-2">
              {(["not_started", "in_progress", "achieved"] as CurriculumStatus[]).map((s) => {
                const c = STATUS_CONFIG[s];
                return (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                      status === s ? c.color + " ring-2 ring-offset-1 ring-current" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label htmlFor={`curriculum-evidence-${domain.key}-${competency.key}`} className="block text-xs font-medium text-slate-600 mb-1.5">Evidence Notes</label>
            <textarea
              id={`curriculum-evidence-${domain.key}-${competency.key}`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Cases, procedures performed, courses attended, WPBA references..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none bg-white"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-700 text-white rounded-xl text-xs font-medium hover:bg-indigo-800 transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              {saving ? "Saving..." : "Save"}
            </button>
            {saved && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Saved</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DoctorCurriculum() {
  const [entries, setEntries] = useState<CurriculumEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedDomain, setExpandedDomain] = useState<string | null>(CURRICULUM[0].key);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/doctor/curriculum");
      if (res.ok) setEntries(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getEntry = (domainKey: string, competencyKey: string) =>
    entries.find((e) => e.domainKey === domainKey && e.competencyKey === competencyKey);

  const getDomainReadiness = (domain: Domain) => {
    const total = domain.competencies.length;
    const achieved = domain.competencies.filter((c) => getEntry(domain.key, c.key)?.status === "achieved").length;
    return { total, achieved, pct: total > 0 ? Math.round((achieved / total) * 100) : 0 };
  };

  const handleSave = async (domainKey: string, competencyKey: string, status: CurriculumStatus, evidenceNotes: string) => {
    const res = await fetch(`/api/doctor/curriculum/${domainKey}/${competencyKey}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, evidenceNotes: evidenceNotes || undefined }),
    });
    if (res.ok) {
      const updated: CurriculumEntry = await res.json();
      setEntries((prev) => {
        const idx = prev.findIndex((e) => e.domainKey === domainKey && e.competencyKey === competencyKey);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = updated;
          return next;
        }
        return [...prev, updated];
      });
    }
  };

  const totalCompetencies = CURRICULUM.reduce((s, d) => s + d.competencies.length, 0);
  const totalAchieved = CURRICULUM.reduce((s, d) => {
    return s + d.competencies.filter((c) => getEntry(d.key, c.key)?.status === "achieved").length;
  }, 0);
  const overallPct = totalCompetencies > 0 ? Math.round((totalAchieved / totalCompetencies) * 100) : 0;

  return (
    <DoctorLayout title="JRCPTB Curriculum Tracker">
      <div className="space-y-6">

        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-900 to-indigo-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="h-8 w-8 text-indigo-300" />
            <div>
              <h2 className="text-xl font-bold">JRCPTB Rheumatology Curriculum Tracker</h2>
              <p className="text-indigo-300 text-sm">2022 Curriculum · ST3–ST7 Rheumatology Training · ARCP Readiness</p>
            </div>
          </div>
          <div className="bg-white/10 rounded-xl px-4 py-3 flex items-center gap-4 text-sm">
            <CheckCircle2 className="h-5 w-5 text-indigo-300" />
            <span>{totalAchieved} of {totalCompetencies} competencies achieved</span>
            <div className="flex-1 bg-white/20 rounded-full h-2 ml-2">
              <div className="bg-indigo-400 h-2 rounded-full transition-all" style={{ width: `${overallPct}%` }} />
            </div>
            <span className="font-bold">{overallPct}%</span>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-2xl" />)}
          </div>
        ) : (
          <div className="space-y-4">
            {CURRICULUM.map((domain) => {
              const readiness = getDomainReadiness(domain);
              const expanded = expandedDomain === domain.key;
              return (
                <div key={domain.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <button
                    onClick={() => setExpandedDomain(expanded ? null : domain.key)}
                    className="w-full flex items-center gap-4 p-5 text-left hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900">{domain.title}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <div className="flex-1 max-w-48 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${readiness.pct === 100 ? "bg-emerald-500" : "bg-indigo-500"}`}
                            style={{ width: `${readiness.pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500">{readiness.achieved}/{readiness.total} achieved · {readiness.pct}% ARCP ready</span>
                      </div>
                    </div>
                    {expanded ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />}
                  </button>

                  {expanded && (
                    <div className="px-5 pb-5 space-y-2 border-t border-slate-100">
                      <div className="pt-3 space-y-2">
                        {domain.competencies.map((comp) => (
                          <CompetencyRow
                            key={comp.key}
                            domain={domain}
                            competency={comp}
                            entry={getEntry(domain.key, comp.key)}
                            onSave={handleSave}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>Attribution:</strong> Curriculum domains and competency areas mapped from the JRCPTB Rheumatology Specialty Training Curriculum 2022 (jrcptb.org.uk). This tracker is a personal planning tool and does not replace the official JRCPTB portfolio (e-Portfolio/SOAR). All competency achievement requires formal sign-off by your Educational Supervisor and documentation in your official JRCPTB ePortfolio.
        </div>
      </div>
    </DoctorLayout>
  );
}
